import React, { useEffect, useState } from 'react';
import { useAppDispatch } from 'hooks';
import { UncontrolledTooltip } from 'reactstrap';
import classNames from 'classnames';

import { Button } from '~components';
import { AlertMessage } from '~components/form-fields/inputs';
import Icon, {
  faChevronLeft,
  faChevronRight,
  faCloudUploadAlt,
  faHardDrive,
  faPlus,
  faTrash,
  faX,
} from '~components/icon';

import { InvoiceFile } from '~support-at-home/models';

import { deleteFile, getFile } from '~support-at-home/actions/supplierInvoices';

import { FilePreview } from './FilePreview';
import { FileUpload } from './FileUpload';

interface ComponentProps {
  onChange: (file: File) => void;
  className?: string;
  isExisting?: boolean;
  selectorFiles: any[];
  onFileDeleted?: () => void;
}

export const FileArea: React.FC<ComponentProps> = ({
  onChange,
  className,
  isExisting = false,
  selectorFiles: files,
  onFileDeleted,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [fileLoadError, setFileLoadError] = useState<{ fileId: string; message: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const dispatch = useAppDispatch();

  const getFileData = async (currentFile: InvoiceFile) => {
    if (!currentFile?.data) {
      try {
        setFileLoadError(null);
        await dispatch(getFile({ id: currentFile.id })).unwrap();
      } catch (err: any) {
        const errorMessage = err?.message || 'Failed to load file';

        setFileLoadError({
          fileId: currentFile.id,
          message: errorMessage,
        });
      }
    }
  };

  const handleDeleteMissingFile = async () => {
    if (!fileLoadError) return;

    setIsDeleting(true);
    try {
      await dispatch(deleteFile({ id: fileLoadError.fileId })).unwrap();
      setFileLoadError(null);
      // Notify parent component to refresh invoice data
      // Index adjustment will happen in useEffect after files array updates
      onFileDeleted?.();
    } catch (err: any) {
      setFileLoadError({
        fileId: fileLoadError.fileId,
        message: err?.message || 'Failed to delete file',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // Enforce index bounds when files array changes (e.g., after deletion)
  useEffect(() => {
    setCurrentIndex((prevIndex) => {
      if (files.length === 0) {
        return 0;
      }
      return Math.min(prevIndex, files.length);
    });
  }, [files.length]);

  useEffect(() => {
    if (isExisting && files.length > 0 && currentIndex < files.length) {
      const currentFile = files[currentIndex];
      if (!currentFile) return;
      getFileData(currentFile);
    }
  }, [files, currentIndex]);

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentIndex < files.length) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const isAtUploadSlot = currentIndex === files.length;
  const shouldShowUploadUI = isAtUploadSlot && !filePreview;

  const renderFileContent = () => {
    const fileData = files[currentIndex]?.data ?? filePreview;
    const fileName = files[currentIndex]?.file_name ?? file?.name;
    const fileType = files[currentIndex]?.file_type ?? file?.type;

    if (shouldShowUploadUI) {
      return (
        <FileUpload
          files={files}
          onChange={onChange}
          setFilePreview={setFilePreview}
          setFile={setFile}
          setCurrentIndex={setCurrentIndex}
          setError={setError}
          isExisting={isExisting}
        />
      );
    }

    if (fileLoadError && fileLoadError.fileId === files[currentIndex]?.id) {
      return (
        <div className="error-content">
          <AlertMessage
            id="supplier-invoices-file-error"
            name="Corrupt or Missing File"
            color="danger"
            value="This file is corrupt or missing. Please delete and re-upload."
            type="alert-danger"
          />
          <div className="d-flex justify-content-center mt-3">
            <Button onClick={handleDeleteMissingFile} disabled={isDeleting} color="danger">
              <Icon icon={faTrash} className="me-2" />
              {isDeleting ? 'Deleting...' : 'Delete File'}
            </Button>
          </div>
        </div>
      );
    }

    if (!fileData) {
      return <div className="no-file">Loading</div>;
    }

    return <FilePreview fileData={fileData} fileType={fileType} fileName={fileName} />;
  };

  const renderUploadStatus = () => {
    if (currentIndex >= files.length && !file) {
      return null;
    }

    // Check if file is in the selector / uploaded files
    const isServerFile = currentIndex < files.length && !!files[currentIndex]?.file_name;

    // Generate unique IDs for the tooltips based on current index
    const tooltipId = isServerFile ? `uploaded-file-icon-${currentIndex}` : `local-file-icon-${currentIndex}`;

    return (
      <>
        <Icon
          icon={isServerFile ? faCloudUploadAlt : faHardDrive}
          id={tooltipId}
          className={isServerFile ? 'cloud-icon' : 'local-icon'}
        />
        <UncontrolledTooltip target={tooltipId}>
          {isServerFile ? 'Uploaded File' : 'Ready to Upload'}
        </UncontrolledTooltip>
      </>
    );
  };

  const containerClassName = classNames('file-area-container', [className]);
  const contentClassName = classNames('content', { upload: shouldShowUploadUI });

  return (
    <div className={containerClassName}>
      {error && (
        <div className="error-container">
          <div className="error-message">
            {error}
            <Button onClick={() => setError(null)}>
              <Icon icon={faX} />
            </Button>
          </div>
        </div>
      )}

      {(isExisting || file || files.length > 0 || filePreview || currentIndex === files.length) && !error && (
        <div className="preview">
          <div className="header">
            <span className="name">
              {currentIndex === files.length
                ? 'Upload a new file'
                : file?.name || files[currentIndex]?.file_name || 'No file selected'}
            </span>
            {renderUploadStatus()}
          </div>

          <div className={contentClassName}>{renderFileContent()}</div>
          {isExisting && (
            <>
              <div className="navigation">
                <Button onClick={handlePrev} disabled={currentIndex === 0} className="nav-button">
                  <Icon icon={faChevronLeft} />
                </Button>
                <div className="dots">
                  {Array.from({ length: files.length + 1 }).map((_, idx) => {
                    if (idx === files.length) {
                      return (
                        <div
                          key={idx}
                          className={classNames('upload-dot', { active: idx === currentIndex })}
                          onClick={() => setCurrentIndex(idx)}
                        >
                          <Icon icon={faPlus} />
                        </div>
                      );
                    }

                    return (
                      <div
                        key={idx}
                        className={classNames('dot', { active: idx === currentIndex })}
                        onClick={() => setCurrentIndex(idx)}
                      />
                    );
                  })}
                </div>
                <Button onClick={handleNext} disabled={currentIndex === files.length} className="nav-button">
                  <Icon icon={faChevronRight} />
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default FileArea;
